import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import {
  Row,
  Col,
  Spin,
  Form,
  Button,
  Input,
  Modal,
  DatePicker,
} from 'antd';
import FormSubmitControls from 'components/FormSubmitControls';
import Table from 'components/Table';
import ModalDelete from 'components/ModalDelete';
import API from 'utils/api';
import { onError, onSuccess } from 'utils/handlers';
import Select from 'components/Select';
import { isMutable } from 'utils/estadosGlobales';
import { selectEstadosGlobales } from 'store/catalogos';
import getFormattedValues, { formatReceived } from 'utils/formatValues';
import ICONOS_DE_SEGUIMIENTO, { IconoDeSeguimiento, getIconoDeSeguimiento } from 'utils/iconosDeSeguimiento';

const baseURI = '/configuraciones/iconografias-de-seguimiento/';
const categoriasURI = '/catalogos/categorias-de-iconografia/';
const modulosURI = '/configuraciones/modulos-de-documentos/';
const documentosOperativosURI = '/configuraciones/documentos-operativos/';

// El Select genérico filtra sobre `option.label`, pero aquí el label es un
// elemento JSX (ícono + texto), no una cadena — se filtra por el texto plano
// del catálogo de íconos en su lugar.
const filterIconOption = (input, option) => {
  const icono = getIconoDeSeguimiento(option.value);
  const label = icono?.label || '';
  return label.toLowerCase().includes(input.toLowerCase());
};

export const permissionIconografiaDeSeguimiento = {
  permissionModel: 'iconografiadeseguimiento',
};

// eslint-disable-next-line react/prop-types
function IconografiaDeSeguimiento({ permission }) {
  const estadosGlobales = useSelector(selectEstadosGlobales);
  const [form] = Form.useForm();
  const moduloWatch = Form.useWatch('modulo', form);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [visible, setVisible] = useState(false);
  const [visibleAlert, setVisibleAlert] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [mutable, setMutable] = useState(true);

  const [categorias, setCategorias] = useState([]);
  const [modulos, setModulos] = useState([]);
  const [documentosOperativos, setDocumentosOperativos] = useState([]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const res = await API.get(baseURI);
      setData(res.data);
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const fetchAll = async () => {
    try {
      setLoading(true);
      const [resCategorias, resModulos, resDocumentos] = await Promise.all([
        API.get(categoriasURI),
        API.get(modulosURI),
        API.get(documentosOperativosURI),
      ]);
      setCategorias(resCategorias.data || []);
      setModulos(resModulos.data || []);
      setDocumentosOperativos(resDocumentos.data || []);
      await fetchData();
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  useEffect(() => {
    fetchAll();
    return () => API.tryCancel;
    // eslint-disable-next-line
  }, []);

  const onCancel = () => {
    setSelectedRowKeys([]);
    form.resetFields();
    setVisible(false);
    setMutable(true);
  };

  const onFinish = async () => {
    try {
      setLoading(true);
      await form.validateFields();
      const values = getFormattedValues(form.getFieldsValue());
      if (!selectedRowKeys.length) {
        const res = await API.post(baseURI, values);
        if (res?.status === 201) {
          onSuccess(res, 'Agregado correctamente');
          onCancel();
          await fetchData();
        }
      } else {
        const [key] = selectedRowKeys;
        const res = await API.put(`${baseURI}${key}/`, values);
        if (res?.status === 200) {
          onSuccess(res, 'Actualizado correctamente');
          onCancel();
          await fetchData();
        }
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const deleteItem = async () => {
    try {
      setLoading(true);
      if (selectedRowKeys.length) {
        const [key] = selectedRowKeys;
        const response = await API.delete(`${baseURI}${key}/`);
        if (response?.status === 204) {
          onSuccess(response, 'Eliminado correctamente');
          setVisibleAlert(false);
          onCancel();
          await fetchData();
        }
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const handleOnRowClick = (record) => {
    setSelectedRowKeys([record.id]);
    form.setFieldsValue(formatReceived(record));
    setMutable(isMutable(record));
  };

  const rowSelection = {
    selectedRowKeys,
    type: 'radio',
  };

  const onClickAdd = () => {
    onCancel();
    setVisible(true);
  };

  const onClickEdit = () => {
    setVisible(true);
  };

  const onClickDelete = () => {
    setVisibleAlert(true);
  };

  const columns = [
    {
      titleText: 'Icono',
      dataIndex: 'icono',
      key: 'icono',
      width: 80,
      render: (val) => <IconoDeSeguimiento name={val} style={{ fontSize: 18 }} />,
    },
    {
      titleText: 'Descripción / Acción',
      dataIndex: 'descripcion',
      key: 'descripcion',
      width: 280,
    },
    {
      titleText: 'Categoría',
      dataIndex: 'categoria_concepto',
      key: 'categoria_concepto',
      width: 150,
    },
    {
      titleText: 'Módulo de Aplicación',
      dataIndex: 'modulo_descripcion',
      key: 'modulo_descripcion',
      width: 180,
    },
    {
      titleText: 'Documento Operativo',
      dataIndex: 'documento_operativo_descripcion',
      key: 'documento_operativo_descripcion',
      width: 200,
    },
    {
      titleText: 'Vigencia Final',
      dataIndex: 'vigencia_final',
      key: 'vigencia_final',
      width: 150,
    },
    {
      titleText: 'Estado',
      dataIndex: 'estados_globales',
      key: 'estados_globales',
      width: 120,
      render: (val) => estadosGlobales.find((e) => e.id === val)?.descripcion,
    },
  ];

  const rules = {
    required: [{ required: true, message: 'El campo es requerido' }],
  };

  return (
    <Row className="container">
      <Spin tip="Cargando..." spinning={loading}>
        <Table
          cols={columns}
          data={data}
          rowSelection={rowSelection}
          handleOnRowClick={handleOnRowClick}
          loading={loading}
          controls={{
            onClickAdd,
            onClickEdit,
            onClickDelete,
          }}
          mobileColIndex={0}
          permission={permission}
        />
        <Modal
          open={visible}
          title={(
            <FormSubmitControls
              label={`${!selectedRowKeys.length ? 'Agregar' : 'Editar'} Iconografía de Seguimiento`}
              onFinish={onFinish}
              onCancel={onCancel}
              mutable={mutable}
            />
          )}
          onCancel={onCancel}
          footer={null}
          closable={false}
          maskClosable={!loading}
          keyboard={!loading}
          forceRender
        >
          <Form
            name="iconografia_de_seguimiento"
            form={form}
            layout="vertical"
            onFinish={onFinish}
            scrollToFirstError
            initialValues={{ estados_globales: 1 }}
          >
            <Row gutter={10}>
              <Col span={24}>
                <Form.Item
                  label="Icono"
                  rules={rules.required}
                  name="icono"
                  hasFeedback
                >
                  <Select
                    dataSource={ICONOS_DE_SEGUIMIENTO}
                    valueProp="value"
                    filterOption={filterIconOption}
                    render={(item) => (
                      <span>
                        <item.Icon style={{ color: item.color, marginRight: 6 }} />
                        {item.label}
                      </span>
                    )}
                    disabled={!mutable}
                  />
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item
                  label="Descripción / Acción"
                  rules={rules.required}
                  name="descripcion"
                  hasFeedback
                >
                  <Input allowClear disabled={!mutable} />
                </Form.Item>
              </Col>
              <Col xs={24} sm={24} md={24}>
                <Form.Item
                  label="Categoría"
                  rules={rules.required}
                  name="categoria"
                  hasFeedback
                >
                  <Select
                    dataSource={categorias}
                    labelProp="concepto"
                    disabled={!mutable}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={24} md={24}>
                <Form.Item
                  label="Módulo de Aplicación"
                  rules={rules.required}
                  name="modulo"
                  hasFeedback
                >
                  <Select
                    dataSource={modulos}
                    labelProp="descripcion"
                    disabled={!mutable}
                    onChange={() => form.resetFields(['documento_operativo'])}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={24} md={24}>
                <Form.Item
                  label="Documento Operativo"
                  rules={rules.required}
                  name="documento_operativo"
                  hasFeedback
                >
                  <Select
                    dataSource={documentosOperativos.filter((e) => e.modulo === moduloWatch)}
                    labelProp="descripcion"
                    disabled={!mutable || !moduloWatch}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={24} md={24}>
                <Form.Item
                  label="Vigencia Final"
                  name="vigencia_final"
                  hasFeedback
                >
                  <DatePicker
                    format="DD/MM/YYYY"
                    placeholder=""
                    style={{ width: '100%' }}
                    disabled={!mutable}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={24} md={24}>
                <Form.Item
                  label="Estado"
                  name="estados_globales"
                  hasFeedback
                >
                  <Select
                    dataSource={estadosGlobales}
                    disabled
                  />
                </Form.Item>
              </Col>
              <Form.Item hidden>
                <Button htmlType="submit" />
              </Form.Item>
            </Row>
          </Form>
        </Modal>
        <ModalDelete
          onDelete={deleteItem}
          onCancel={() => setVisibleAlert(false)}
          open={visibleAlert}
          content="La iconografía de seguimiento seleccionada"
          loading={loading}
        />
      </Spin>
    </Row>
  );
}

export default IconografiaDeSeguimiento;
